<template>
    <div class="w-full">
        <div class="b_clear_banner" :class="{ no_hero: isHeroHidden }">
            <hero-banner
                v-if="!isHeroHidden"
                :image="heroImage"
                :limit-width="content?.hero?.limitWidth"
            />

            <div class="container">
                <back-button v-if="!$device.isDesktop" :items="breadcrumbs" />

                <div class="banner_content">
                    <div class="banner_content_inner w100">
                        <div class="content">
                            <breadcrumbs
                                v-if="$device.isDesktop"
                                :items="breadcrumbs"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="b_event">
            <div class="page_decor page_decor_right">
                <div class="image_decor">
                    <picture v-full-image-path:assets="'decor_event_01.svg'" />
                </div>
            </div>
            <div class="page_decor page_decor_left">
                <div class="image_decor">
                    <picture v-full-image-path:assets="'decor_event_02.svg'" />
                </div>
            </div>
            <div class="container">
                <div class="page_title">
                    <h1>{{ content.general.title }}</h1>
                </div>
                <div class="short_content">
                    <p v-html="content.general.description" />
                </div>
                <div class="search_wrapper search_wrapper_category">
                    <div class="search_form relative">
                        <input
                            v-model="query"
                            class="form_control"
                            type="text"
                            :placeholder="$t('drugs.searchPlaceholder')"
                            @keyup.enter="search"
                        />
                        <button
                            class="btn_reset btn_search"
                            type="button"
                            @click="fetchItems"
                        >
                            {{ $t('drugs.searchButton') }}
                        </button>
                    </div>
                </div>
                <div class="b_categories_tabs">
                    <div class="categories_tabs js-tabs-wrapper">
                        <div class="tabs_top">
                            <div class="tabs_list tabs">
                                <vue-horizontal
                                    :rtl="isRtl"
                                    :displacement="0.5"
                                    :button-between="false"
                                >
                                    <div
                                        v-for="(letter, index) in alphabet"
                                        :key="`letter-${index}`"
                                    >
                                        <div
                                            class="tab"
                                            :class="{
                                                active:
                                                    letter === selectedLetter,
                                                disabled:
                                                    !medicationsByLetter[letter]
                                                        .length
                                            }"
                                            @click="setSelectedLetter(letter)"
                                        >
                                            {{ letter }}
                                        </div>
                                    </div>
                                </vue-horizontal>
                            </div>
                        </div>
                        <div class="tabs_content_wrapper">
                            <div class="tab_content active">
                                <div class="content">
                                    <div class="links_wrapper flex">
                                        <div
                                            v-for="category in currentCategoryItems"
                                            :key="category.id"
                                            class="link_row"
                                        >
                                            <nuxt-link
                                                :to="
                                                    localePath(
                                                        `/drugs/${category.slug}`
                                                    )
                                                "
                                                class="link"
                                            >
                                                <div class="icon">
                                                    <picture
                                                        v-full-image-path:assets="
                                                            getRandomIcon()
                                                        "
                                                    />
                                                </div>
                                                <div class="title">
                                                    {{ category.name }}
                                                </div>
                                            </nuxt-link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { StatusCodes } from 'http-status-codes';

import headMixin from '@/mixins/head.mixin.js';
import getRandomIcon from '@/helpers/getRandomIcon';
import VueHorizontal from '@/components/VueHorizontal';
import BackButton from '@/components/elements/BackButton';
import HeroBanner from '@/components/elements/HeroBanner';
import Breadcrumbs from '@/components/elements/Breadcrumbs';

export default {
    components: {
        BackButton,
        HeroBanner,
        Breadcrumbs,
        VueHorizontal
    },

    mixins: [headMixin],

    async asyncData({ store: { dispatch }, query: { q = '' }, error }) {
        try {
            const [
                { content, categories, meta },
                { rows: medicationCategories }
            ] = await Promise.all([
                dispatch('contents/show', 'drugsPage'),
                dispatch('medicationCategories/index', {
                    search: q
                })
            ]);

            return {
                content,
                categories,
                meta,
                medicationCategories,
                selectedLetter: null,
                query: ''
            };
        } catch (err) {
            if (err.response?.status !== StatusCodes.INTERNAL_SERVER_ERROR) {
                return error({ statusCode: err.response.status });
            }

            console.error(err);

            return error({ statusCode: StatusCodes.INTERNAL_SERVER_ERROR });
        }
    },

    computed: {
        ...mapGetters(['alphabet', 'isRtl']),

        headTitle() {
            return this.content.general?.title;
        },

        currentCategoryItems() {
            return this.medicationsByLetter[this.selectedLetter];
        },

        breadcrumbs() {
            return [
                {
                    text: this.$t('homepage.home'),
                    to: this.localePath('/')
                },
                ...this.$getCategoryBreadcrumb(this.categories),
                {
                    text: this.$t('drugs.pageTitle'),
                    active: true
                }
            ];
        },

        medicationsByLetter() {
            const medicationsByLetter = {};

            for (const letter of this.alphabet.split('')) {
                medicationsByLetter[letter] = [];
            }

            for (const category of this.medicationCategories) {
                if (!medicationsByLetter[category.name[0].toLowerCase()]) {
                    continue;
                }

                medicationsByLetter[category.name[0].toLowerCase()].push(
                    category
                );
            }

            return medicationsByLetter;
        },

        heroImage() {
            return this.$device.isDesktop
                ? this.content.hero.image
                : this.content.hero.mobileImage;
        },

        isHeroHidden() {
            return this.content?.hero?.hidden;
        }
    },

    watch: {
        medicationsByLetter: {
            deep: true,
            handler() {
                if (process.server) {
                    return;
                }

                const letterWithMostResults = Object.keys(
                    this.medicationsByLetter
                ).reduce((acc, letter) => {
                    return this.medicationsByLetter[letter].length >
                        this.medicationsByLetter[acc].length
                        ? letter
                        : acc;
                }, this.alphabet[0]);

                this.setSelectedLetter(letterWithMostResults);
            }
        }
    },

    created() {
        this.selectedLetter = this.alphabet[0];
    },

    methods: {
        ...mapActions({
            fetchMedicationCategories: 'medicationCategories/index'
        }),

        search() {
            this.medicationCategories = [];

            this.fetchItems();
        },

        setSelectedLetter(letter) {
            const hasItems = !!this.medicationsByLetter[letter].length;

            if (hasItems) {
                this.selectedLetter = letter;
            }
        },

        getRandomIcon() {
            return getRandomIcon();
        },

        onSelectedResult(slug) {
            const { path } = this.$route;

            this.$router.push(this.localePath(`${path}/${slug}`));
        },

        async fetchItems() {
            try {
                const { rows } = await this.fetchMedicationCategories({
                    search: this.query
                });

                this.medicationCategories = rows;
            } catch (error) {
                console.error(error);

                this.$toasted.global.error();
            }
        }
    }
};
</script>
